import { ReactNode, useId, cloneElement, isValidElement } from 'react';
import { cn } from '@/lib/utils';

/*
 * FormField — DESIGN_SYSTEM.md §2.1
 *
 * Composes Label + control + HelperText/ErrorText with locked spacing
 * (mb-3.5 = 14px) so forms don't drift visually.
 *
 * Usage:
 *   <FormField label="Email" required helperText="We'll never share it">
 *     <Input type="email" />
 *   </FormField>
 *
 * Auto-wires htmlFor/id and aria-describedby/invalid into the child if it
 * accepts them. Pass `id` explicitly to override.
 */

interface FormFieldProps {
  label?: ReactNode;
  /** Show "(opt)" muted suffix next to label */
  optional?: boolean;
  /** Show red asterisk next to label */
  required?: boolean;
  helperText?: ReactNode;
  error?: ReactNode;
  /** Explicit id; otherwise auto-generated */
  id?: string;
  className?: string;
  children: ReactNode;
}

export function FormField({
  label,
  optional,
  required,
  helperText,
  error,
  id,
  className,
  children,
}: FormFieldProps) {
  const reactId = useId();
  const childId = isValidElement(children)
    ? ((children.props as Record<string, unknown>).id as string | undefined)
    : undefined;
  const fieldId = id ?? childId ?? reactId;
  const helperId = helperText ? `${fieldId}-helper` : undefined;
  const errorId = error ? `${fieldId}-error` : undefined;
  const describedBy = errorId ?? helperId;

  let control = children;
  if (isValidElement(children)) {
    const childProps = children.props as Record<string, unknown>;
    const existingDescribedBy = childProps['aria-describedby'] as string | undefined;
    control = cloneElement(children as React.ReactElement<Record<string, unknown>>, {
      id: childProps.id ?? fieldId,
      'aria-describedby': [existingDescribedBy, describedBy].filter(Boolean).join(' ') || undefined,
      'aria-invalid': error ? true : childProps['aria-invalid'],
    });
  }

  return (
    <div className={cn('mb-3.5', className)}>
      {label && (
        <label
          htmlFor={fieldId}
          className="mb-1.5 inline-flex items-center gap-1 text-label text-ink"
        >
          {label}
          {required && <span className="text-danger" aria-hidden="true">*</span>}
          {optional && <span className="text-text-muted font-normal">(opt)</span>}
        </label>
      )}
      {control}
      {error ? (
        <p id={errorId} className="mt-1.5 text-body-xs text-danger" role="alert">
          {error}
        </p>
      ) : helperText ? (
        <p id={helperId} className="mt-1.5 text-body-xs text-soft">
          {helperText}
        </p>
      ) : null}
    </div>
  );
}
